import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const teacherTools = [
  {
    phase: '课前',
    items: [
      { label: '大纲建议', desc: '大纲是否围绕核心概念展开', icon: 'ri-file-text-line' },
      { label: 'PPT建议', desc: 'PPT是否有清晰的问题链', icon: 'ri-slideshow-line' },
      { label: '案例推荐', desc: '案例是否能支撑理论', icon: 'ri-booklet-line' },
    ],
    color: 'accent' as const,
  },
  {
    phase: '课后',
    items: [
      { label: '导入有效性', desc: '哪段导入有效，哪处可优化', icon: 'ri-play-circle-line' },
      { label: '案例关联', desc: '哪处案例可关联权威资料', icon: 'ri-link-m' },
      { label: '等待时间', desc: '哪个问题后可多留十秒', icon: 'ri-timer-line' },
      { label: '讲稿备注', desc: '哪段讲稿可补充出处', icon: 'ri-sticky-note-line' },
    ],
    color: 'primary' as const,
  },
];

export default function P17EvidenceTeacher({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Left - Title side */}
          <div className="lg:col-span-4">
            <div ref={titleRef}>
              <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                服务教师
              </span>
              <h2 className={`font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                证据<span className="text-primary-600">对教师</span>的价值
              </h2>
              <p className={`text-sm text-foreground-500 font-body leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
                AI评课不是一张"结果单"，更像一个贯穿课前、课中、课后的教学助手
              </p>
            </div>

            {/* 教师助手概念配图 */}
            <div className={`mt-6 rounded-2xl overflow-hidden border border-primary-200/20 shadow-sm transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20isometric%20illustration%20of%20a%20teacher%20working%20with%20AI%20digital%20assistant%20in%20classroom%2C%20floating%20educational%20icons%20and%20lesson%20plan%20documents%2C%20soft%20blue%20and%20gold%20color%20palette%2C%20clean%20modern%20design%2C%20minimalist%20background%2C%20educational%20technology%20concept%20art%2C%20professional%20quality&width=600&height=420&seq=teacher-ai-assist-01&orientation=landscape"
                alt="AI教学助手"
                className="w-full h-auto object-cover"
              />
            </div>

            <div className={`mt-6 p-5 rounded-2xl glass-gold border-accent-200/30 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-sparkling-line text-accent-500" />
                <span className="text-xs font-body font-semibold text-accent-700">对优秀教师</span>
              </div>
              <p className="text-sm text-foreground-600 font-body leading-relaxed">
                这些材料能沉淀为<strong className="text-accent-700">可迁移方法</strong>
              </p>
              <div className="mt-3 pt-3 border-t border-accent-200/30">
                <div className="flex items-center gap-2">
                  <i className="ri-seedling-line text-primary-500" />
                  <span className="text-xs font-body font-semibold text-primary-700">对青年教师</span>
                </div>
                <p className="text-sm text-foreground-500 font-body mt-1">可学习的<strong className="text-primary-700">具体样本</strong></p>
              </div>
            </div>
          </div>

          {/* Right - Timeline flow */}
          <div className="lg:col-span-8">
            <div className="relative">
              {/* 垂直时间轴线 */}
              <div className="absolute left-6 top-10 bottom-10 w-px border-l-2 border-dashed border-primary-300/40 hidden lg:block" />

              <div className="space-y-8">
                {teacherTools.map((phase, phaseIdx) => {
                  const isAccent = phase.color === 'accent';
                  return (
                    <div key={phase.phase} className="relative">
                      {/* 阶段节点标题 */}
                      <div className="flex items-center gap-4 mb-5">
                        <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-md ${
                          isAccent ? 'bg-accent-500' : 'bg-primary-500'
                        }`}>
                          <i className={`${phase.phase === '课前' ? 'ri-sun-line' : 'ri-moon-line'} text-xl text-white`} />
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-heading text-xl font-bold ${isAccent ? 'text-accent-700' : 'text-primary-700'}`}>{phase.phase}</h4>
                          <p className="text-xs text-foreground-400 font-body mt-0.5">
                            {phase.phase === '课前' ? 'AI辅助备课，从大纲到案例全流程支持' : '课后智能复盘，精准定位教学优化点'}
                          </p>
                        </div>
                      </div>

                      {/* 工具项卡片流 */}
                      <div className="pl-0 lg:pl-16">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {phase.items.map((item, i) => (
                            <div
                              key={item.label}
                              className={`group relative p-5 rounded-xl transition-all duration-500 hover:-translate-y-1 ${
                                isAccent
                                  ? 'bg-accent-50/40 border border-accent-200/50 hover:border-accent-300/70 hover:shadow-md hover:shadow-accent-100/20'
                                  : 'bg-primary-50/40 border border-primary-200/50 hover:border-primary-300/70 hover:shadow-md hover:shadow-primary-100/20'
                              }`}
                              style={{ transitionDelay: `${phaseIdx * 200 + i * 80}ms` }}
                            >
                              {/* 编号角标 */}
                              <div className={`absolute -top-2 -left-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm ${
                                isAccent ? 'bg-accent-400' : 'bg-primary-400'
                              }`}>
                                {phaseIdx === 0 ? i + 1 : i + 4}
                              </div>

                              <div className="flex items-start gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                  isAccent
                                    ? 'bg-accent-50 border border-accent-200/50'
                                    : 'bg-primary-50 border border-primary-200/50'
                                }`}
                                >
                                  <i className={`${item.icon} text-base ${isAccent ? 'text-accent-600' : 'text-primary-600'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-body font-bold text-foreground-800 mb-1">{item.label}</p>
                                  <p className="text-xs text-foreground-500 font-body leading-relaxed">{item.desc}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 阶段间连接线指示（箭头） */}
                      {phaseIdx === 0 && (
                        <div className="hidden lg:flex items-center justify-center my-4 pl-16">
                          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/50">
                            <i className="ri-arrow-down-line text-primary-400 text-sm" />
                            <span className="text-xs font-body text-foreground-500">授课完成后自动进入课后分析</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}